/**
 * IMPORTS
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Bell, CheckCheck, Volume2, VolumeX } from "lucide-react";

import {
  useContagemNotificacoes,
  useListaNotificacoes,
  useMarcarNotificacoes,
  usePreferenciaSomNotificacoes,
} from "@/hooks/conversa-cliente/use-notificacoes";

import { FEATURES, useFeature } from "@/utils/auth/features";
import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError } from "@/utils/notify-messages";

import { NotificacaoLinha } from "./notificacao-linha";
import { useAbrirNotificacao } from "./use-abrir-notificacao";

const QUANTIDADE_NO_SINO = 8;

/** Sino com contador e as últimas notificações. Só aparece com `acessar_notificacoes`. */
function NotificacoesSino() {
  const habilitado = useFeature(FEATURES.ACESSAR_NOTIFICACOES);
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: naoLidas = 0 } = useContagemNotificacoes();
  const lista = useListaNotificacoes(QUANTIDADE_NO_SINO, habilitado && aberto);
  const { marcarTodas } = useMarcarNotificacoes();
  const { somAtivo, alternarSom } = usePreferenciaSomNotificacoes();
  const abrirNotificacao = useAbrirNotificacao(() => setAberto(false));

  // fecha ao clicar fora ou apertar Esc
  useEffect(() => {
    if (!aberto) return;
    const aoClicar = (evento: MouseEvent) => {
      if (!containerRef.current?.contains(evento.target as Node)) setAberto(false);
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  if (!habilitado) return null;

  const notificacoes = lista.data?.pages[0]?.data ?? [];

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setAberto((atual) => !atual)}
        className="relative flex size-10 items-center justify-center rounded-full text-gray-700 hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-custom900"
        aria-label={naoLidas > 0 ? `Notificações: ${naoLidas} não lidas` : "Notificações"}
        aria-expanded={aberto}
      >
        <Bell className="size-6" />
        {naoLidas > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-[11px] font-semibold leading-none text-white">
            {naoLidas > 99 ? "99+" : naoLidas}
          </span>
        )}
      </button>

      {aberto && (
        <div className="absolute right-0 z-50 mt-2 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
          <header className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Notificações</h2>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={alternarSom}
                aria-pressed={somAtivo}
                aria-label={
                  somAtivo ? "Silenciar som das notificações" : "Ativar som das notificações"
                }
                title={somAtivo ? "Som ativado" : "Som desativado"}
                className="rounded-md p-1.5 text-gray-600 hover:bg-gray-100"
              >
                {somAtivo ? <Volume2 className="size-4" /> : <VolumeX className="size-4" />}
              </button>
              <button
                type="button"
                disabled={naoLidas === 0 || marcarTodas.isPending}
                onClick={() =>
                  marcarTodas.mutate(undefined, {
                    onError: (erro) => notifyError(getApiErrorMessage(erro)),
                  })
                }
                className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40"
              >
                <CheckCheck className="size-3.5" />
                Marcar todas como lidas
              </button>
            </div>
          </header>

          <div className="max-h-96 overflow-y-auto p-1">
            {lista.isLoading ? (
              <div className="space-y-2 p-3">
                {[0, 1, 2].map((indice) => (
                  <div key={indice} className="h-12 animate-pulse rounded-md bg-gray-100" />
                ))}
              </div>
            ) : lista.isError ? (
              <p className="p-6 text-center text-sm text-gray-500">
                Não foi possível carregar as notificações.
              </p>
            ) : notificacoes.length === 0 ? (
              <p className="p-6 text-center text-sm text-gray-500">Você não tem notificações.</p>
            ) : (
              notificacoes.map((notificacao) => (
                <NotificacaoLinha
                  key={notificacao.id}
                  notificacao={notificacao}
                  onAbrir={abrirNotificacao}
                  compacta
                />
              ))
            )}
          </div>

          <footer className="border-t border-gray-200 p-1">
            <button
              type="button"
              onClick={() => {
                setAberto(false);
                navigate("/notificacoes");
              }}
              className="w-full rounded-md py-2 text-sm font-medium text-orange-600 hover:bg-orange-50"
            >
              Ver todas
            </button>
          </footer>
        </div>
      )}
    </div>
  );
}

/**
 * EXPORTS
 */
export { NotificacoesSino };
